/**
 * De dónde viene la persona que está en pantalla.
 *
 * Este archivo no manda nada a ninguna parte: describe. Cuál es el producto
 * del que habla la pantalla, de qué muro salió el login que se está viendo, y
 * si el navegador va dentro de la app de Instagram. Quien lo manda a GA4 es
 * `services/analytics.js`, que importa de acá; la flecha va en un solo
 * sentido y conviene que siga así.
 *
 * Se separó el 9-sep-2026. Antes vivía todo junto en `analytics.js`, que
 * había quedado con dos temas: los eventos que se emiten y la descripción de
 * quién los emite. Un archivo con dos temas se lee entero cada vez que hay
 * que tocar uno de los dos.
 */

/**
 * Los siete productos, con la MISMA clave que usan la ruta `<clave>-demo`, el
 * `teaser_component` de backend/marketing/pages.py y el bundle del teaser.
 *
 * No es una segunda lista: es la misma, escrita en el otro idioma porque
 * Python y JavaScript no pueden compartir un archivo. La copia canónica está
 * en backend/marketing/analitica.py y hay un test que falla si las dos dejan
 * de coincidir.
 */
const PRODUCTOS = [
  "test-correction",
  "planification",
  "eureka",
  "individual-documents",
  "class-materials",
  "materials",
  "pie",
];

/**
 * Los otros nombres con que el sistema llama a los mismos productos.
 *
 * `redpie` es como el SPA llama al Sistema PIE en `changeView` y en la ruta de
 * venta, mientras que la demo y el teaser lo llaman `pie`. Las etiquetas con
 * mayúscula son las del chatbot, que son las claves de `SERVICE_PREFIX` en el
 * backend y lo que viaja en `service` por la API.
 *
 * OJO con los dos de materiales: «Pruebas Informales» es `materials` y
 * «Materiales para la clase» es `class-materials`. Los dos cobran contra
 * `materials_uses`, que es de donde viene la confusión.
 */
const ALIAS = {
  redpie: "pie",
  PIE: "pie",
  Eureka: "eureka",
  Planificaciones: "planification",
  "Pruebas Informales": "materials",
  "Materiales para la clase": "class-materials",
  "Documentos PIE": "individual-documents",
  "Corregir Test": "test-correction",
};

/**
 * El producto de una ruta del SPA, o "" si esa ruta no es la demo de ninguno.
 *
 * Recibe el hash en vez de leerlo de `window` para poder contestar la misma
 * pregunta sobre una ruta que NO es la de la pantalla: `medirClicDePortada`
 * le pasa el destino de un enlace para saber si lleva a una demo. Sin
 * argumento sigue siendo lo de siempre —la ruta actual—, que es como la llama
 * `productoActual`.
 *
 * POR QUÉ NO UN `-demo` SUELTO DEL OTRO LADO. "¿Esto es la demo de un
 * producto?" tiene que contestarse en un solo lugar y contra la misma lista:
 * una expresión regular repetida en `analytics.js` diría que sí a
 * `#/cualquier-cosa-demo`, y el día que aparezca el octavo producto una de
 * las dos copias se queda vieja sin que nada falle.
 */
export function productoDeLaRuta(hash) {
  const crudo = hash === undefined ? window.location.hash : hash;
  // `#/eureka-demo`, y también `#/test-correction-demo?invita=<token>`.
  const ruta = String(crudo || "").split("?")[0];
  const encontrado = /^#\/([a-z-]+)-demo$/.exec(ruta);
  if (!encontrado) return "";
  return PRODUCTOS.indexOf(encontrado[1]) !== -1 ? encontrado[1] : "";
}

/**
 * El producto del que habla la pantalla, o "" si no habla de ninguno.
 *
 * Primero la ruta, porque es lo que no se puede equivocar: si estás en
 * `#/eureka-demo`, el producto es Eureka aunque el componente no lo sepa.
 * Después el servicio que el llamador tenga a mano, normalizado.
 */
export function productoActual(service) {
  const deLaRuta = productoDeLaRuta();
  if (deLaRuta) return deLaRuta;
  const valor = String(service || "").trim();
  if (!valor) return "";
  if (PRODUCTOS.indexOf(valor) !== -1) return valor;
  return ALIAS[valor] || "";
}

/**
 * De dónde viene un alta, para que el servidor la escriba en la base.
 *
 * Son los dos datos que el servidor NO puede averiguar solo: el producto y la
 * ruta del SPA viven después del `#`, y lo que va después del numeral no
 * llega nunca a Django. Los `utm_*` y el `fbclid` sí llegan —viajan en la
 * query real— y los levanta `OrigenMiddleware`; acá no se tocan.
 *
 * Por qué hace falta guardarlo en la base y no basta con GA4: GA4 pierde del
 * lado del navegador cerca de 4 de cada 5 `sign_up`, así que la pregunta
 * «cuántas cuentas trajo este anuncio» no se puede responder ahí.
 * Ver backend/marketing/atribucion.py.
 *
 * `producto` es el MISMO valor que ya se manda en `login_wall_shown`, para
 * que el embudo se pueda cruzar sin traducir nada.
 */
export function origenDelAlta(service) {
  return {
    producto: productoActual(service),
    // Sin el `#` y sin la query: ahí viaja el `?invita=` de los referidos, que
    // no es parte de la ruta.
    ruta: String(window.location.hash || "")
      .replace(/^#/, "")
      .split("?")[0],
  };
}

/**
 * Navegadores que van dentro de otra app (Instagram, Facebook, TikTok).
 *
 * MISMA LISTA que `_INTERNOS` en `backend/marketing/atribucion.py`. Son dos copias
 * porque Python y JavaScript no pueden compartir un archivo, no porque sean
 * dos criterios: si cambias una, cambia la otra. Un test comprueba que digan
 * lo mismo en cuanto las dos vivan en la misma rama.
 *
 * Por qué importa: del 1 al 8-sep-2026, 335 personas vieron el muro de login
 * dentro del navegador de la app de Instagram y 16 apretaron Google. La
 * hipótesis es que Google Identity Services no funciona en navegadores
 * embebidos (`disallowed_useragent`). Sin esta marca en los eventos, esos 335
 * son indistinguibles del resto y la hipótesis no se puede ni confirmar ni
 * descartar.
 *
 * `wv)` es el Android WebView, que es como se anuncia el navegador de una app
 * en Android. No es un parser de user agents ni pretende serlo: la pregunta es
 * de grano grueso y el costo de un falso positivo es una etiqueta, no un
 * bloqueo — nada se le esconde a nadie por esto.
 */
const NAVEGADORES_INTERNOS = [
  "Instagram",
  "FBAN",
  "FBAV",
  "FB_IAB",
  "TikTok",
  "Line/",
  "wv)",
];

/**
 * ¿El navegador va dentro de otra app?
 *
 * Sin argumento lee el User-Agent del navegador. Se le puede pasar uno para
 * poder probarla, que es la única razón por la que recibe un parámetro.
 */
export function navegadorInterno(userAgent) {
  const ua = String(
    userAgent === undefined
      ? (window.navigator && window.navigator.userAgent) || ""
      : userAgent || ""
  );
  return NAVEGADORES_INTERNOS.some((marca) => ua.indexOf(marca) !== -1);
}

/**
 * El aviso que le explica a quien está dentro de Instagram cómo salir.
 *
 * APAGADO A PROPÓSITO. La hipótesis —que Google Identity Services no funciona
 * en navegadores embebidos— todavía no está confirmada: Javier la está
 * probando con un teléfono. Mientras no lo esté, mostrarle a 335 personas al
 * mes un aviso que quizá no explique su problema es empeorarles la pantalla
 * por una corazonada.
 *
 * Se enciende poniendo esto en `true`. Lo que sí está medido desde ya es el
 * `navegador_interno` de cada evento y el `login_failure` con
 * `reason: "gis_no_disponible"`, que son los dos números con los que se
 * decide encenderlo.
 */
export const AVISO_NAVEGADOR_INTERNO = false;

/**
 * De dónde salió el muro de login que está en pantalla.
 *
 * Los valores son los caminos por los que a alguien se le pide iniciar sesión:
 * `trial`, `demo_button` y `service`, los muros de las demos y los servicios;
 * `ticket` e `invitacion`, las dos tarjetas del layout; y `reset`, el login
 * que se abre al volver de «Olvidé mi contraseña» (`?login=clave`). Un valor
 * nuevo se agrega acá, y `backend/tests/test_eventos_login.py` lo busca.
 *
 * SE LLAMABA `source` Y ESO ROMPÍA LA ATRIBUCIÓN. GA4 trata un parámetro
 * llamado `source` como fuente de campaña: en cuanto alguien llegaba al muro,
 * la sesión pasaba a tener `sessionManualSource = trial` y perdía al anuncio
 * que la había traído. Desde el 1-sep-2026 hay 86 sesiones así, sin un solo
 * `utm` en la URL, y por eso en las sesiones atribuidas a anuncios hay cero
 * `login_attempt` y cero `login_success`. Los cinco nombres que GA4 se
 * reserva —`source`, `medium`, `campaign`, `content`, `term`— no se pueden
 * usar como parámetros de evento; la lista y el porqué están en
 * `backend/marketing/analitica.py`.
 *
 * Se recuerda entre eventos porque el muro y el intento de login ocurren en
 * componentes distintos —el layout abre el modal, el modal emite el intento— y
 * sin esto no hay forma de saber cuántos de los que vieron el muro de `trial`
 * llegaron a apretar el botón, que es justo el paso que se quiere medir.
 */
let origenLoginRecordado = "";

export function recordarOrigenLogin(valor) {
  origenLoginRecordado = String(valor || "");
}

/**
 * Se olvida al cerrarse el modal y al entrar.
 *
 * Sin esto, alguien que ve el muro de `trial`, lo cierra, y media hora después
 * entra desde el menú, quedaría contado como una conversión del muro de
 * `trial`. Un origen ausente se nota en el informe; uno equivocado, no.
 */
export function olvidarOrigenLogin() {
  origenLoginRecordado = "";
}

export function origenLoginActual() {
  return origenLoginRecordado;
}

export default {
  productoActual,
  origenDelAlta,
  navegadorInterno,
  AVISO_NAVEGADOR_INTERNO,
  recordarOrigenLogin,
  olvidarOrigenLogin,
  origenLoginActual,
};
